<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="currentVisible"
    width="900px"
    :before-close="handleClose"
    class="audit-record-dialog"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    custom-class="custom-dialog"
  >
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading"></i>
      <span>加载中...</span>
    </div>

    <!-- 审核记录列表 -->
    <div v-else class="audit-record-container">
      <!-- 表头 -->
      <div class="table-header">
        <div class="header-cell nickname">昵称</div>
        <div class="header-cell action">动作</div>
        <div class="header-cell status">状态</div>
        <div class="header-cell reason">原因</div>
        <div class="header-cell time">时间</div>
      </div>

      <!-- 记录列表 -->
      <div class="record-list" ref="recordList">
        <div v-for="record in auditRecords" :key="record.id" class="record-item">
          <div class="record-cell nickname">
            <span class="nickname-text">{{ record.nickName || '未知用户' }}</span>
          </div>
          <div class="record-cell action">
            <span class="action-text">修改信息</span>
          </div>
          <div class="record-cell status">
            <span :class="getStatusClass(record.status)" class="status-text">
              {{ getStatusText(record.status) }}
            </span>
          </div>
          <div class="record-cell reason">
            <span class="reason-text">{{ record.refuseReason || '-' }}</span>
          </div>
          <div class="record-cell time">
            <span class="time-text">{{ formatTime(record.createTime) }}</span>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="auditRecords.length === 0" class="empty-state">
          <i class="el-icon-document"></i>
          <span>暂无审核记录</span>
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button type="primary" @click="handleClose" class="submit-btn">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import {getAuditRecordList} from '@/api/consultingService'

export default {
  name: 'AuditRecordDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    consultantId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      auditRecords: []
    }
  },
  computed: {
    // 弹层标题（带图标）
    dialogTitle() {
      return '审核记录'
    },
    currentVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.consultantId) {
        this.loadAuditRecords()
      }
    }
  },
  methods: {
    // 加载审核记录
    async loadAuditRecords() {
      this.loading = true
      try {
        const response = await getAuditRecordList({
          consultantId: this.consultantId
        })

        if (response.code === 200) {
          // 按时间降序排列
          this.auditRecords = response.data.sort((a, b) => {
            return new Date(b.createTime) - new Date(a.createTime)
          })
        } else {
          this.$message.error(response.msg || '获取审核记录失败')
          this.auditRecords = []
        }
      } catch (error) {
        console.error('获取审核记录失败:', error)
        this.$message.error('获取审核记录失败，请重试')
        this.auditRecords = []
      } finally {
        this.loading = false
      }
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        0: '审核中',
        1: '通过',
        2: '不通过'
      }
      return statusMap[status] || '未知'
    },

    // 获取状态样式类
    getStatusClass(status) {
      const classMap = {
        0: 'status-pending', // 审核中 - 灰色
        1: 'status-approved', // 通过 - 绿色
        2: 'status-rejected' // 不通过 - 红色
      }
      return classMap[status] || 'status-unknown'
    },

    // 格式化时间
    formatTime(timeStr) {
      if (!timeStr) return '-'
      const date = new Date(timeStr)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${month}.${day} ${hours}:${minutes}`
    },

    // 关闭弹窗
    handleClose() {
      this.currentVisible = false
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .custom-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    padding: 24px 40px 20px;
    border-bottom: none;

    .el-dialog__title {
      font-size: 20px;
      font-weight: 600;
      color: #333;
      display: flex;
      align-items: center;
      gap: 8px;

      &::before {
        content: '';
        display: inline-block;
        width: 24px;
        height: 24px;
        background-image: url('/img/bianji.png');
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
      }
    }

    .el-dialog__headerbtn {
      top: 24px;
      right: 40px;
    }
  }

  .el-dialog__body {
    padding: 24px 60px;
  }

  .el-dialog__footer {
    padding: 20px 40px 32px;
    border-top: none;
  }
}

.audit-record-dialog {
  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #999;

    i {
      margin-right: 8px;
      font-size: 16px;
    }
  }

  .audit-record-container {
    .table-header {
      display: flex;
      background-color: #f5f7fa;
      border-radius: 4px 4px 0 0;
      font-weight: 600;
      color: #606266;

      .header-cell {
        padding: 12px 16px;
        border-right: 1px solid #ebeef5;

        &:last-child {
          border-right: none;
        }

        &.nickname {
          flex: 0 0 120px;
        }

        &.action {
          flex: 0 0 100px;
        }

        &.status {
          flex: 0 0 100px;
        }

        &.reason {
          flex: 1;
        }

        &.time {
          flex: 0 0 120px;
        }
      }
    }

    .record-list {
      max-height: 400px;
      overflow-y: auto;
      border: 1px solid #ebeef5;
      border-top: none;
      border-radius: 0 0 4px 4px;

      .record-item {
        display: flex;
        border-bottom: 1px dashed #ebeef5;

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background-color: #f5f7fa;
        }

        .record-cell {
          padding: 12px 16px;
          border-right: 1px solid #ebeef5;
          display: flex;
          align-items: center;

          &:last-child {
            border-right: none;
          }

          &.nickname {
            flex: 0 0 120px;
          }

          &.action {
            flex: 0 0 100px;
          }

          &.status {
            flex: 0 0 100px;
          }

          &.reason {
            flex: 1;
            word-break: break-all;
            line-height: 1.4;
          }

          &.time {
            flex: 0 0 120px;
          }

          .nickname-text,
          .action-text,
          .time-text {
            color: #606266;
            font-size: 14px;
          }

          .reason-text {
            color: #606266;
            font-size: 14px;
            word-break: break-all;
            line-height: 1.4;
          }

          .status-text {
            font-size: 14px;
            font-weight: 500;

            &.status-pending {
              color: #909399; // 灰色 - 审核中
            }

            &.status-approved {
              color: #67c23a; // 绿色 - 通过
            }

            &.status-rejected {
              color: #f56c6c; // 红色 - 不通过
            }

            &.status-unknown {
              color: #909399; // 灰色 - 未知状态
            }
          }
        }
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        color: #999;

        i {
          font-size: 48px;
          margin-bottom: 16px;
          color: #c0c4cc;
        }

        span {
          font-size: 14px;
        }
      }
    }
  }

  .dialog-footer {
    text-align: center;
    padding: 0;

    .submit-btn {
      width: 60%;
      height: 48px;
      font-size: 18px;
      font-weight: 500;
      border-radius: 8px;
      background: #5ad3cd;
      border-color: #5ad3cd;

      &:hover {
        background: #4ab8b2;
        border-color: #4ab8b2;
      }
    }
  }
}

// 滚动条样式
.record-list::-webkit-scrollbar {
  width: 6px;
}

.record-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.record-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.record-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
